/**
 * SERVICIO DE EMPRESAS CON SUPABASE
 * 
 * Este módulo proporciona operaciones CRUD para empresas usando los endpoints REST de Supabase.
 * Incluye funcionalidades para crear, leer, actualizar y eliminar empresas.
 * 
 * Características principales:
 * - Operaciones CRUD completas
 * - Autenticación con tokens JWT de Supabase
 * - Validación de datos
 * - Manejo de errores
 * - Filtros por diferentes campos
 *
 */

// URLs base para los endpoints REST de Supabase
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Servicio de empresas que maneja todas las operaciones CRUD
 * relacionadas con la gestión de empresas
 */
const empresaService = {
    /**
     * Obtiene el token de autenticación desde localStorage
     * @returns {string|null} Token de acceso o null si no existe
     */
    getAuthToken() {
        return localStorage.getItem('access_token');
    },

    /**
     * Obtiene los headers necesarios para las peticiones autenticadas
     * @returns {Object} Headers con autenticación
     */
    getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
        };
    },

    /**
     * Obtiene el ID del distribuidor del usuario actual
     * @returns {string|null} ID del distribuidor o null si no existe
     */
    getCurrentDistribuidorId() {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return null;

            const user = JSON.parse(userStr);
            return user.distribuidorData?.id_distribuidor || null;
        } catch (error) {
            console.error('Error al obtener ID de distribuidor:', error);
            return null;
        }
    },

    /**
     * Transforma los datos del formulario al formato requerido por la API
     * @param {Object} formData - Datos del formulario
     * @returns {Object} Datos transformados para la API
     */
    transformFormDataToAPI(formData) {
        const distribuidorId = this.getCurrentDistribuidorId();

        return {
            nombre_empresa: formData.nombreEmpresa || '',
            nit: formData.nit || '',
            correo: formData.correo || '',
            telefono: formData.telefono || '',
            direccion: formData.direccion || '',
            ciudad: formData.ciudad || '',
            persona_contacto: formData.personaContacto || '',
            cargo: formData.cargo || '',
            comentarios: formData.comentarios || '',
            id_distribuidor: distribuidorId
        };
    },

    /**
     * Transforma los datos de la API al formato del formulario
     * @param {Object} apiData - Datos de la API
     * @returns {Object} Datos transformados para el formulario
     */
    transformAPIDataToForm(apiData) {
        return {
            nombreEmpresa: apiData.nombre_empresa || '',
            nit: apiData.nit || '',
            correo: apiData.correo || '',
            telefono: apiData.telefono || '',
            direccion: apiData.direccion || '',
            ciudad: apiData.ciudad || '',
            personaContacto: apiData.persona_contacto || '',
            cargo: apiData.cargo || '',
            comentarios: apiData.comentarios || ''
        };
    },

    /**
     * Obtiene todas las empresas
     * 
     * @returns {Promise<Array>} Lista de empresas
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     * @example
     * const empresas = await empresaService.getAllEmpresas();
     */
    async getAllEmpresas() {
        try {
            const url = `${SUPABASE_URL}/rest/v1/company`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener empresas' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener empresas:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Obtiene empresas filtradas por NIT
     * 
     * @param {string} nit - NIT de la empresa
     * @returns {Promise<Array>} Lista de empresas filtradas
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     * @example
     * const empresas = await empresaService.getEmpresasByNit('123456789');
     */
    async getEmpresasByNit(nit) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/company?nit=eq.${nit}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener empresas por NIT' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener empresas por NIT:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Obtiene empresas filtradas por ciudad
     * 
     * @param {string} ciudad - Ciudad
     * @returns {Promise<Array>} Lista de empresas filtradas
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     * @example
     * const empresas = await empresaService.getEmpresasByCity('Bogotá');
     */
    async getEmpresasByCity(ciudad) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/company?ciudad=eq.${encodeURIComponent(ciudad)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener empresas por ciudad' };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener empresas por ciudad:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Obtiene una empresa específica por ID
     * 
     * @param {string} empresaId - ID de la empresa
     * @returns {Promise<Object>} Datos de la empresa
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     * @example
     * const empresa = await empresaService.getEmpresaById('uuid-here');
     */
    async getEmpresaById(empresaId) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener empresa' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error al obtener empresa:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Crea una nueva empresa
     * 
     * @param {Object} empresaData - Datos de la empresa
     * @returns {Promise<Object>} Empresa creada
     * @throws {Object} Error con mensaje descriptivo si falla la creación
     * 
     * @example
     * const nuevaEmpresa = await empresaService.createEmpresa(formData);
     */
    async createEmpresa(empresaData) {
        try {
            const transformedData = this.transformFormDataToAPI(empresaData);

            if (!transformedData.nombre_empresa || !transformedData.nit || !transformedData.correo) {
                throw { message: 'Faltan campos obligatorios: nombre de la empresa, NIT y correo' };
            }

            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';

            const response = await fetch(`${SUPABASE_URL}/rest/v1/company`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(transformedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al crear empresa' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;

        } catch (error) {
            console.error('Error al crear empresa:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Actualiza una empresa existente
     * 
     * @param {string} empresaId - ID de la empresa
     * @param {Object} empresaData - Datos actualizados de la empresa
     * @returns {Promise<Object>} Empresa actualizada
     * @throws {Object} Error con mensaje descriptivo si falla la actualización
     * 
     * @example
     * const empresaActualizada = await empresaService.updateEmpresa('uuid-here', formData);
     */
    async updateEmpresa(empresaId, empresaData) {
        try {
            const transformedData = this.transformFormDataToAPI(empresaData);

            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';

            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: headers,
                body: JSON.stringify(transformedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al actualizar empresa' };
            }

            const data = await response.json();
            return data.length > 0 ? data[0] : null;

        } catch (error) {
            console.error('Error al actualizar empresa:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Elimina una empresa
     * 
     * @param {string} empresaId - ID de la empresa a eliminar
     * @returns {Promise<boolean>} True si se eliminó correctamente
     * @throws {Object} Error con mensaje descriptivo si falla la eliminación
     * 
     * @example
     * const eliminado = await empresaService.deleteEmpresa('uuid-here');
     */
    async deleteEmpresa(empresaId) {
        try {
            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al eliminar empresa' };
            }

            return true;
        } catch (error) {
            console.error('Error al eliminar empresa:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Crea múltiples empresas desde un archivo Excel
     * 
     * @param {Array} empresasArray - Array de empresas desde Excel
     * @returns {Promise<Object>} Resultado de la operación masiva
     * @throws {Object} Error con mensaje descriptivo si falla la creación
     * 
     * @example
     * const resultado = await empresaService.createBulkEmpresas(excelData);
     */
    async createBulkEmpresas(empresasArray) {
        try {
            const results = {
                success: 0,
                errors: [],
                total: empresasArray.length
            };

            for (let i = 0; i < empresasArray.length; i++) {
                try {
                    const empresaData = this.transformExcelDataToForm(empresasArray[i]);
                    await this.createEmpresa(empresaData);
                    results.success++;
                } catch (error) {
                    results.errors.push({
                        row: i + 1,
                        error: error.message || 'Error desconocido'
                    });
                }
            }

            return results;
        } catch (error) {
            console.error('Error en carga masiva:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Transforma datos del Excel al formato del formulario
     * @param {Object} excelRow - Fila del Excel
     * @returns {Object} Datos transformados para el formulario
     */
    transformExcelDataToForm(excelRow) {
        return {
            nombreEmpresa: excelRow["Nombre de la empresa"] || '',
            nit: excelRow["NIT"]?.toString() || '',
            correo: excelRow["Correo"] || '',
            telefono: excelRow["Telefono"]?.toString() || '',
            direccion: excelRow["Direccion"] || '',
            ciudad: excelRow["Ciudad"] || '',
            personaContacto: excelRow["Persona de contacto principal"] || '',
            cargo: excelRow["Cargo"] || '',
            comentarios: excelRow["Comentarios"] || ''
        };
    }
};

export default empresaService;